(() => {
  if (customElements.get('nexvey-youtube-reviews')) return;
  class NexveyYouTubeReviews extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.dialog = this.querySelector('dialog');
      this.track = this.querySelector('.nyr__track');
      if (!this.dialog || !this.track) return;
      this.player = this.querySelector('.nyr__player');
      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-nyr-model]');
        if (tab) this.selectModel(tab);
        const play = event.target.closest('[data-nyr-video]');
        if (play && this.contains(play)) this.openVideo(play);
        if (event.target.closest('[data-nyr-close]')) this.dialog.close();
        if (event.target.closest('[data-nyr-prev]')) this.move(-1);
        if (event.target.closest('[data-nyr-next]')) this.move(1);
      }, options);
      this.querySelector('.nyr__tabs')?.addEventListener('keydown', (event) => {
        const tabs = Array.from(this.querySelectorAll('[data-nyr-model]'));
        const index = tabs.indexOf(event.target);
        if (index < 0 || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        this.selectModel(tabs[next]);
        tabs[next].focus();
      }, options);
      document.addEventListener('shopify:block:select', (event) => {
        const target = event.target;
        if (!(target instanceof Element) || !this.contains(target)) return;
        const card = target.closest('[data-nyr-card-model]');
        if (card) {
          const tab = Array.from(this.querySelectorAll('[data-nyr-model]')).find(t => t.dataset.nyrModel === card.dataset.nyrCardModel);
          if (tab) this.selectModel(tab);
        }
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.dialog.addEventListener('close', () => this.cleanPlayer(), options);
      this.track.addEventListener('scroll', () => this.updateControls(), { ...options, passive: true });
      this.resize = new ResizeObserver(() => this.updateControls());
      this.resize.observe(this.track);
      this.updateControls();
      const initial = this.querySelector('[data-nyr-model][aria-selected="true"]');
      if (initial) this.selectModel(initial);
    }
    stopInlineVideos() {
      this.querySelectorAll('.nyr__media iframe:not([data-nyr-native-src])').forEach(frame => frame.remove());
      this.querySelectorAll('.nyr__trigger').forEach(button => {button.hidden = false; button.setAttribute('aria-expanded','false');});
    }
    selectModel(tab) {
      if (this.dialog.open) this.dialog.close();
      this.stopInlineVideos();
      this.querySelectorAll('[data-nyr-model]').forEach(item => {const active=item===tab;item.setAttribute('aria-selected',String(active));item.tabIndex=active?0:-1;});
      this.querySelectorAll('[data-nyr-card-model]').forEach(card => {card.hidden=card.dataset.nyrCardModel!==tab.dataset.nyrModel;});
      this.querySelectorAll('[data-nyr-native-src]').forEach(frame => {
        const active = !frame.closest('[data-nyr-card-model]').hidden;
        if (active && frame.getAttribute('src') !== frame.dataset.nyrNativeSrc) {
          frame.setAttribute('src', frame.dataset.nyrNativeSrc);
        } else if (!active && frame.hasAttribute('src')) {
          frame.removeAttribute('src');
        }
      });
      this.querySelector('.nyr__panel').setAttribute('aria-labelledby',tab.id);
      const visible = Array.from(this.track.querySelectorAll('.nyr__card')).filter(card=>!card.hidden);
      this.querySelector('.nyr__empty').hidden=visible.length>0;
      this.track.hidden=visible.length===0;
      this.track.scrollLeft=0;
      this.updateControls();
    }
    openVideo(button) {
      if (this.dialog.open) return;
      const id = button.dataset.nyrVideo;
      if (!/^[a-zA-Z0-9_-]{11}$/.test(id)) return;
      this.trigger = button;
      const title = button.dataset.nyrTitle || 'Full ride review';
      this.querySelector('[data-nyr-dialog-title]').textContent = title;
      this.querySelector('[data-nyr-watch]').href = `https://www.youtube.com/watch?v=${id}`;
      const iframe = document.createElement('iframe');
      iframe.title = title;
      iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      this.player.replaceChildren(iframe);
      this.previousOverflow = document.documentElement.style.overflow;
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      button.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-nyr-close]').focus();
    }
    cleanPlayer() {
      this.player?.replaceChildren();
      if (this.previousOverflow !== undefined) {
        document.documentElement.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'false');
        if (this.trigger.isConnected) this.trigger.focus();
        this.trigger = null;
      }
    }
    move(direction) {
      const card = this.track.querySelector('.nyr__card:not([hidden])');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
    updateControls() {
      const controls = this.querySelector('.nyr__controls');
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (!controls) return;
      controls.hidden = this.track.hidden || max < 4;
      this.querySelector('[data-nyr-prev]').disabled = this.track.scrollLeft < 4;
      this.querySelector('[data-nyr-next]').disabled = this.track.scrollLeft >= max - 4;
    }
    disconnectedCallback() {
      this.querySelectorAll('[data-nyr-native-src]').forEach(frame => frame.removeAttribute('src'));
      if (this.dialog?.open) this.dialog.close();
      this.cleanPlayer();
      this.stopInlineVideos();
      this.abort?.abort();
      this.resize?.disconnect();
    }
  }
  customElements.define('nexvey-youtube-reviews', NexveyYouTubeReviews);
})();
